import { useLocation } from '@remix-run/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';
import { toSnakeCase } from '../../helpers/format/snake-case';
import type { UUID } from '../../helpers/validate/django-uuid';
import { FD_USER_TYPE_TO_PYLON_USER_TYPE } from './pylon-helper';
import type { PylonPMRole, PylonPortalAccessModel } from './types';

type PylonForms =
  | 'general-support-request'
  | 'denial-dispute'
  | 'consumer-forms'
  | 'underwriting-request'
  | 'pet-verification-question';

export type GlobalContextProps = {
  user?: {
    type: 'tenant' | 'manager' | 'underwriter';
    salesforce_account_id?: string | null;
    hubspot_company_id?: string | null;
  };
  userRole?: {
    pk: UUID;
    name: string;
  } | null;
  portfolio?: {
    id?: UUID | null;
    display_name: string;
    is_cra_portfolio: boolean;
  };
  portfolioConfig?: {
    pm_custom: { [key: string]: SerializableValue };
    applicant_custom: { [key: string]: SerializableValue };
  };
  allPylonPortalAccess?: PylonPortalAccessModel[];
};

type GlobalContext = {
  referer: string | null;
  salesforceAccountId?: string | null;
  hubspotCompanyId?: string | null;
  isCRAPortfolio: boolean;
  portfolioId: UUID | null;
  portfolioName: string;
  isTermwise: boolean;
  isDecisionAssist: boolean;
  isPetVerification: boolean;
  isPortfolioAppRemodel: boolean;
  pmRole: PylonPMRole | null;
  userType: 'property manager' | 'applicant' | 'underwriter';
};

type PageContext = {
  unitId?: UUID;
  unitAddress?: string;
  userappGroupId?: UUID;
  applicationLink?: string | null;
  userApplicationId?: UUID;
  isUaAppRemodel?: boolean;
};

type PylonCustomFields = {
  global: Partial<GlobalContext>;
  page: Partial<PageContext> | null;
};

type PylonContextProps = {
  setGlobalContext: (
    globalContextProps: Omit<GlobalContextProps, 'referer'>
  ) => void;
  setPageContext: (pageContextProps: PageContext | null) => void;
};

const PylonContext = createContext<PylonContextProps | null>(null);

export function getCurrentReferer(pathname: string): string {
  return typeof window !== 'undefined' ? window.location.href : pathname;
}

export function usePylonContext() {
  const context = useContext(PylonContext);
  if (!context) {
    throw new Error(
      'usePylonContext must be used within <PylonContextProvider />'
    );
  }
  return context;
}

export function PylonContextProvider({
  preloaded,
  user,
  userRole,
  portfolio,
  portfolioConfig,
  allPylonPortalAccess,
  children,
}: PropsWithChildren<GlobalContextProps & { preloaded: boolean }>) {
  const location = useLocation();

  const [customContext, setCustomContext] = useState<PylonCustomFields>({
    global: {
      referer: getCurrentReferer(location.pathname),
    },
    page: null,
  });

  useEffect(() => {
    const currentReferer = getCurrentReferer(location.pathname);

    if (currentReferer !== customContext.global.referer) {
      setCustomContext((prev) => ({
        global: { ...prev.global, referer: currentReferer },
        page: prev.page,
      }));
    }
  }, [location.pathname, customContext.global.referer]);

  const setVisibleTicketForms = useCallback((context: PylonCustomFields) => {
    if (window.Pylon) {
      const ticketForms: PylonForms[] = [
        'general-support-request',
        'denial-dispute',
      ];
      if (context.global.isCRAPortfolio) {
        ticketForms.push('consumer-forms');
      }
      if (context.global.isDecisionAssist) {
        ticketForms.push('underwriting-request');
      }
      if (context.global.isPetVerification) {
        ticketForms.push('pet-verification-question');
      }
      window.Pylon('setVisibleTicketForms', ticketForms);
    }
  }, []);

  const setGlobalContext = useCallback(
    (globalContextProps: Omit<GlobalContextProps, 'referer'>) => {
      const {
        user,
        userRole,
        portfolio,
        portfolioConfig,
        allPylonPortalAccess,
      } = globalContextProps;
      const userPylonPortalAccess = (allPylonPortalAccess ?? []).find(
        (access) => access.user_role_id === userRole?.pk
      );

      const globalContext: Partial<GlobalContext> = {
        salesforceAccountId: user?.salesforce_account_id,
        hubspotCompanyId: user?.hubspot_company_id,
        isCRAPortfolio: portfolio?.is_cra_portfolio,
        portfolioId: portfolio?.id,
        portfolioName: portfolio?.display_name,
        isTermwise: Boolean(
          portfolioConfig?.pm_custom.security_deposit_alternatives_enabled
        ),
        isDecisionAssist: Boolean(portfolioConfig?.pm_custom.is_uwaas_enabled),
        isPetVerification: Boolean(
          portfolioConfig?.applicant_custom.pet_check_configuration_enabled
        ),
        isPortfolioAppRemodel: Boolean(
          portfolioConfig?.pm_custom.is_portfolio_app_remodel
        ),
        pmRole: userPylonPortalAccess?.pm_pylon_role ?? undefined,
        userType: FD_USER_TYPE_TO_PYLON_USER_TYPE[user?.type ?? 'tenant'],
      };

      setCustomContext((prev) => {
        const updatedCustomContext = {
          global: { ...prev.global, ...globalContext },
          page: prev.page,
        };

        if (preloaded && window.Pylon) {
          setVisibleTicketForms(updatedCustomContext);
        }

        return updatedCustomContext;
      });
    },
    [preloaded, setVisibleTicketForms]
  );

  useEffect(() => {
    if (
      user != null &&
      userRole != null &&
      portfolio != null &&
      portfolioConfig != null
    ) {
      setGlobalContext({
        user,
        userRole,
        portfolio,
        portfolioConfig,
        allPylonPortalAccess,
      });
    }
  }, [
    user,
    userRole,
    portfolio,
    portfolioConfig,
    allPylonPortalAccess,
    setGlobalContext,
  ]);

  const setPageContext = useCallback((pageContextProps: PageContext | null) => {
    setCustomContext((prev) => ({
      global: prev.global,
      page:
        pageContextProps != null ? { ...prev.page, ...pageContextProps } : null,
    }));
  }, []);

  useEffect(() => {
    if (window.Pylon) {
      const customFields = Object.entries({
        ...customContext.global,
        ...(customContext.page ?? {}),
      }).reduce((acc, [key, value]) => {
        acc[toSnakeCase(key)] = value;
        return acc;
      }, {} as Record<string, SerializableValue>);
      window.Pylon('setNewIssueCustomFields', customFields);
    }
  }, [customContext.global, customContext.page]);

  return (
    <PylonContext.Provider
      value={{
        setGlobalContext,
        setPageContext,
      }}
    >
      {children}
    </PylonContext.Provider>
  );
}
